/**
 * Case O Cute — kawaii behaviours (vanilla, no deps)
 * 1. Device memory  — remembers the shopper's iPhone model, auto-selects it on PDPs
 * 2. Sticky mobile ATC — shows a bottom bar when the buy button scrolls away
 * 3. Free-shipping bar + bundle nudge — live updates on cart changes
 * 4. Confetti — a small celebration when something is added to the bag
 * All motion respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  var CART_LINES_UPDATE = 'shopify:cart:lines-update';
  var STORAGE_KEY = 'coc:iphone-model';
  var FREE_SHIP_PAISE = 49900; // ₹499
  var BUNDLE_MIN_QTY = 2;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  function getModel() {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
  }
  function setModel(value) {
    try { localStorage.setItem(STORAGE_KEY, value); } catch (e) { /* private mode */ }
  }
  function formatINR(paise) {
    return '₹' + Math.round(paise / 100).toLocaleString('en-IN');
  }
  function fetchCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  /* ---------- 1. device memory ---------- */
  function findModelSelects() {
    // Horizon dropdown variant picker renders <select> elements inside variant-picker;
    // ours has a single "iPhone Model" option.
    return Array.prototype.slice
      .call(document.querySelectorAll('variant-picker select, [data-variant-picker] select'))
      .filter(function (sel) { return /iphone/i.test(sel.innerHTML); });
  }

  function initDeviceMemory() {
    var selects = findModelSelects();

    selects.forEach(function (sel) {
      // restore
      var saved = getModel();
      if (saved) {
        var match = Array.prototype.find.call(sel.options, function (o) {
          return o.value === saved && !o.disabled;
        });
        if (match && sel.value !== saved) {
          sel.value = saved;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      // remember
      sel.addEventListener('change', function () {
        if (/iphone/i.test(sel.value)) setModel(sel.value);
      });
    });

    // radio/button style fallback
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (input && input.matches && input.matches('variant-picker input[type="radio"]')) {
        if (/iphone/i.test(input.value)) setModel(input.value);
      }
    });

    // homepage device tiles remember the shopper's generation
    document.addEventListener('click', function (e) {
      var tile = e.target && e.target.closest && e.target.closest('[data-coc-device]');
      if (tile && tile.getAttribute('data-coc-device')) {
        setModel(tile.getAttribute('data-coc-device'));
      }
    });
  }

  /* ---------- 2. sticky mobile ATC ---------- */
  function initStickyAtc() {
    var bar = document.querySelector('.coc-sticky-atc');
    if (!bar) return;
    var realButton = document.querySelector(
      'product-form-component button[type="submit"], .product-form-buttons button[type="submit"], form[action*="/cart/add"] button[type="submit"]'
    );
    if (!realButton) return;

    var proxy = bar.querySelector('.coc-sticky-atc__btn');
    if (proxy) {
      proxy.addEventListener('click', function () {
        realButton.click();
      });
    }

    if (!('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            bar.removeAttribute('data-visible');
          } else if (entry.boundingClientRect.top < 0) {
            bar.setAttribute('data-visible', '');
          }
        });
      },
      { threshold: 0 }
    );
    observer.observe(realButton);
  }

  /* ---------- 3. free-shipping bar + bundle nudge ---------- */
  function renderCartExtras(cart) {
    document.querySelectorAll('[data-coc-cart-count]').forEach(function (b) {
      b.textContent = cart.item_count;
      b.hidden = cart.item_count === 0;
    });

    var bars = document.querySelectorAll('.coc-ship-bar');
    var nudges = document.querySelectorAll('.coc-bundle-nudge');
    var total = cart.total_price;
    var remaining = FREE_SHIP_PAISE - total;
    var pct = Math.max(0, Math.min(100, (total / FREE_SHIP_PAISE) * 100));

    bars.forEach(function (bar) {
      var label = bar.querySelector('[data-ship-label]');
      var fill = bar.querySelector('.coc-ship-bar__fill');
      if (fill) fill.style.width = pct + '%';
      if (label) {
        if (total <= 0) {
          label.textContent = 'Free shipping on orders over ' + formatINR(FREE_SHIP_PAISE);
          bar.classList.remove('coc-ship-bar--unlocked');
        } else if (remaining > 0) {
          label.textContent = formatINR(remaining) + ' away from free shipping';
          bar.classList.remove('coc-ship-bar--unlocked');
        } else {
          label.textContent = 'free shipping unlocked!';
          bar.classList.add('coc-ship-bar--unlocked');
        }
      }
    });

    var caseCount = cart.items
      .filter(function (item) { return item.product_type === 'Phone Case'; })
      .reduce(function (sum, item) { return sum + item.quantity; }, 0);

    nudges.forEach(function (nudge) {
      if (caseCount === 1) {
        nudge.hidden = false;
      } else {
        nudge.hidden = true;
      }
    });
  }

  function initCartExtras() {
    if (!document.querySelector('.coc-ship-bar')) return;
    fetchCart().then(renderCartExtras).catch(function () {});
    document.addEventListener(CART_LINES_UPDATE, function () {
      fetchCart().then(renderCartExtras).catch(function () {});
    });
  }

  /* ---------- 4. confetti on add-to-cart ---------- */
  var lastItemCount = null;
  function burstConfetti() {
    if (reducedMotion) return;
    var canvas = document.createElement('canvas');
    canvas.className = 'coc-confetti';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    var colors = ['#FFD6E8', '#E8D5FF', '#D4F5E9', '#FFF5E1', '#F48FB1', '#D6608E'];
    var parts = [];
    for (var i = 0; i < 48; i++) {
      parts.push({
        x: canvas.width / 2 + (Math.random() - 0.5) * 160,
        y: canvas.height * 0.35,
        vx: (Math.random() - 0.5) * 9,
        vy: -(4 + Math.random() * 7),
        size: 5 + Math.random() * 6,
        color: colors[i % colors.length],
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3
      });
    }
    var start = null;
    function frame(ts) {
      if (!start) start = ts;
      var elapsed = ts - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      parts.forEach(function (p) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.28;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        ctx.restore();
      });
      if (elapsed < 1100) {
        requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    }
    requestAnimationFrame(frame);
  }

  function initConfetti() {
    fetchCart().then(function (cart) { lastItemCount = cart.item_count; }).catch(function () {});
    document.addEventListener(CART_LINES_UPDATE, function () {
      fetchCart()
        .then(function (cart) {
          if (lastItemCount !== null && cart.item_count > lastItemCount) burstConfetti();
          lastItemCount = cart.item_count;
        })
        .catch(function () {});
    });
  }

  /* ---------- boot ---------- */
  function init() {
    initDeviceMemory();
    initStickyAtc();
    initCartExtras();
    initConfetti();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ---------- reels strip: unhide when real videos exist, prune 404s ---------- */
(function () {
  var strip = document.querySelector('[data-coc-reels]');
  if (!strip) return;
  var tiles = strip.querySelectorAll('.coc-reels__tile');
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var v = e.target.querySelector('video');
      if (!v) return;
      if (e.isIntersecting) { v.play().catch(function () {}); } else { v.pause(); }
    });
  }, { threshold: 0.35 });
  tiles.forEach(function (tile) {
    var v = tile.querySelector('video');
    if (!v) { tile.remove(); return; }
    v.addEventListener('loadedmetadata', function () {
      strip.hidden = false;
      io.observe(tile);
    });
    v.addEventListener('error', function () { tile.remove(); });
  });
})();

/* ---------- iOS scroll-bleed lock: mirror Horizon's html[scroll-lock] into a body position:fixed lock ---------- */
(function () {
  var docEl = document.documentElement;
  var savedY = 0;
  function sync() {
    var locked = docEl.hasAttribute('scroll-lock');
    var body = document.body;
    if (locked && !body.classList.contains('coc-lock')) {
      savedY = window.scrollY;
      body.style.top = -savedY + 'px';
      body.classList.add('coc-lock');
    } else if (!locked && body.classList.contains('coc-lock')) {
      body.classList.remove('coc-lock');
      body.style.top = '';
      window.scrollTo(0, savedY);
    }
  }
  new MutationObserver(sync).observe(docEl, { attributes: true, attributeFilter: ['scroll-lock'] });
  sync();
})();
